import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, X, Search } from "lucide-react";
import { toast } from "react-toastify";
import CalendarIcon from "../../../../assets/calender-icon.svg";
import PrintIcon from "../../../../assets/icons/PrintIcon";
import GreenCheck from "../../../../assets/icons/GreenCheck";
import {
  productAPI,
  customersAPI,
  imeiAPI,
  vendorAPI,
  returnRegisterAPI,
  getInStockProductsList,
} from "../../../../services/index";
import PrintReturnRegister from "./PrintReturnRegister";
import "../../../../style/returnsRegister.css";
import { useSelector } from "react-redux";

// Custom hook for click outside
function useClickOutside(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onClose]);
}

// Format date to YYYY-MM-DD for input
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Format date to DD/MM/YYYY for display (e.g., "01/10/2025")
const formatDateForDisplay = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${year}`;
};

// Convert DD/MM/YYYY format back to YYYY-MM-DD for API
const convertDateToAPIFormat = (dateString) => {
  if (!dateString) return "";

  // Check if already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Parse DD/MM/YYYY format
  const parts = dateString.split("/");
  if (parts.length === 3) {
    const day = parts[0].padStart(2, "0");
    const month = parts[1].padStart(2, "0");
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }

  // Fallback: try to parse as Date and format
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return formatDateForInput(date);
    }
  } catch (e) {
    // If parsing fails, return as is
  }

  return dateString;
};

function AddManualReturnsModal({ isOpen, onClose, onSubmit, editReturn }) {
  const { isPartsURL } = useSelector((state) => state.domain);
  const [returnId, setReturnId] = useState("");
  const [imei, setImei] = useState("");
  const [buyingDate, setBuyingDate] = useState("");
  const [showBuyingDatePicker, setShowBuyingDatePicker] = useState(false);
  const [buyingDateObj, setBuyingDateObj] = useState(new Date());
  const [buyingDateMonth, setBuyingDateMonth] = useState(new Date());
  const buyingDatePickerRef = useRef(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [defectNote, setDefectNote] = useState("");
  const [action, setAction] = useState("");
  const [, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingReturnData, setLoadingReturnData] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [savedReturnData, setSavedReturnData] = useState(null);

  // IMEI validation states
  const [imeiFound, setImeiFound] = useState(false);
  const [imeiChecking, setImeiChecking] = useState(false);
  const [imeiRecord, setImeiRecord] = useState(null); // Store IMEI record data

  // Error states
  const [defectNoteError, setDefectNoteError] = useState("");

  // Additional fields for "Change Device" action
  const [exchangeModel, setExchangeModel] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [secondLevelAction, setSecondLevelAction] = useState("");
  const [buyingPrice, setBuyingPrice] = useState("");

  // Dropdown states
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [isExchangeModelDropdownOpen, setIsExchangeModelDropdownOpen] =
    useState(false);
  const [isSecondLevelActionDropdownOpen, setIsSecondLevelActionDropdownOpen] =
    useState(false);
  const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [exchangeModelSearch, setExchangeModelSearch] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");

  // Data states
  const [models, setModels] = useState([]);
  const [exchangeModels, setExchangeModels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingExchangeModels, setLoadingExchangeModels] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Refs
  const modelDropdownRef = useRef(null);
  const customerDropdownRef = useRef(null);
  const actionDropdownRef = useRef(null);
  const exchangeModelDropdownRef = useRef(null);
  const secondLevelActionDropdownRef = useRef(null);
  const vendorDropdownRef = useRef(null);
  const modelSearchInputRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const exchangeModelSearchInputRef = useRef(null);

  useClickOutside(modelDropdownRef, () => setIsModelDropdownOpen(false));
  useClickOutside(customerDropdownRef, () => setIsCustomerDropdownOpen(false));
  useClickOutside(actionDropdownRef, () => setIsActionDropdownOpen(false));
  useClickOutside(exchangeModelDropdownRef, () =>
    setIsExchangeModelDropdownOpen(false)
  );
  useClickOutside(secondLevelActionDropdownRef, () =>
    setIsSecondLevelActionDropdownOpen(false)
  );
  useClickOutside(vendorDropdownRef, () => setIsVendorDropdownOpen(false));
  useClickOutside(buyingDatePickerRef, () => setShowBuyingDatePicker(false));

  // Check if in edit mode
  const isEditMode = !!editReturn;

  // Check if IMEI exists in API
  const checkImeiExists = async (imeiValue) => {
    if (!imeiValue || imeiValue.trim() === "") {
      setImeiFound(false);
      setImeiRecord(null);
      return;
    }

    setImeiChecking(true);
    try {
      const response = await imeiAPI.getImeiList(imeiValue.trim(), 1, 50);

      // Handle paginated response structure
      // If count > 0 or results array has items, IMEI was found
      const count = response?.count || 0;
      const imeiData =
        response?.results ||
        response?.data ||
        (Array.isArray(response) ? response : []);

      // IMEI is found if:
      // 1. Count > 0 (API found matching records), OR
      // 2. Results array has items (non-paginated response with results)
      const found =
        count > 0 || (Array.isArray(imeiData) && imeiData.length > 0);

      setImeiFound(found);

      // Store the first IMEI record if found
      if (found && Array.isArray(imeiData) && imeiData.length > 0) {
        setImeiRecord(imeiData[0]);
      } else {
        setImeiRecord(null);
      }
    } catch (error) {
      console.error("Error checking IMEI:", error);
      setImeiFound(false);
      setImeiRecord(null);
    } finally {
      setImeiChecking(false);
    }
  };

  // Validate IMEI with debounce
  useEffect(() => {
    if (!imei || imei.trim() === "") {
      setImeiFound(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      checkImeiExists(imei);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [imei]);

  // Clear change device fields when IMEI not found
  useEffect(() => {
    if (isEditMode) return;
    if (
      !imeiChecking &&
      imei &&
      imei.trim() !== "" &&
      !imeiFound &&
      action === "change_device"
    ) {
      setExchangeModel(null);
      setSecondLevelAction("");
      setSelectedVendor(null);
    }
  }, [imeiFound, imeiChecking, imei, isEditMode, action]);

  // IMEI validation state - only valid if found in API (not just numeric format)
  const isImeiValid = imei.trim() !== "" && imeiFound && !imeiChecking;

  // Action options - filter based on IMEI found status
  const actionOptions = imeiFound
    ? [
        { value: "change_device", label: "Change Device" },
        { value: "keep_for_repair", label: "Keep for Repair" },
        { value: "not_repairable", label: "Not Repairable" },
      ]
    : [{ value: "keep_for_repair", label: "Keep for Repair" }];

  // Second Level Action options (for Change Device)
  const secondLevelActionOptions = [
    { value: "keep_for_vendor", label: "Keep for Supplier" },
    { value: "keep_for_repair", label: "Keep for Repair" },
  ];

  // Fetch models (for Select Model dropdown - uses getProductsList)
  const fetchModels = async (search = "") => {
    setLoadingModels(true);
    try {
      const response = await productAPI.getProductsList({
        search,
        page: 1,
        page_size: 50,
      });

      let productsList = [];
      if (response) {
        if (Array.isArray(response)) {
          productsList = response;
        } else if (response.results && Array.isArray(response.results)) {
          productsList = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          productsList = response.data;
        }
      }

      // Format products for display: Name and Suffix on first line, Ram | Storage | Variants | Status on second line
      const formattedModels = productsList.map((product) => {
        const line1Parts = [];
        if (product.name) line1Parts.push(product.name);
        if (product.suffix) line1Parts.push(product.suffix);
        const line1 = line1Parts.filter((p) => p).join(" ");

        const line2Parts = [];
        if (product.ram) line2Parts.push(product.ram);
        if (product.storage) line2Parts.push(product.storage + "GB");
        if (
          product.variants &&
          Array.isArray(product.variants) &&
          product.variants.length > 0
        ) {
          const variantValues = product.variants
            .map((v) => Object.values(v).join(", "))
            .join(", ");
          if (variantValues) line2Parts.push(variantValues);
        }
        line2Parts.push(product.used_mobile ? "Used" : "New");
        const line2 = line2Parts.filter((p) => p).join(" | ");

        return {
          id: product.id,
          name: line1 + "\n" + line2,
          product: product,
        };
      });

      setModels(formattedModels);
    } catch (error) {
      console.error("Error fetching models:", error);
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Fetch exchange models (for Select Exchange Model dropdown - uses getInStockProductsList)
  const fetchExchangeModels = async (search = "") => {
    setLoadingExchangeModels(true);
    try {
      const response = await getInStockProductsList({
        search,
        page: 1,
        page_size: 50,
      });

      let productsList = [];
      if (response) {
        if (Array.isArray(response)) {
          productsList = response;
        } else if (response.results && Array.isArray(response.results)) {
          productsList = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          productsList = response.data;
        }
      }

      // Format products for display: Name and Suffix on first line, Ram | Storage | Variants | Status on second line
      const formattedModels = productsList.map((product) => {
        const line1Parts = [];
        if (product.name) line1Parts.push(product.name);
        if (product.suffix) line1Parts.push(product.suffix);
        const line1 = line1Parts.filter((p) => p).join(" ");

        const line2Parts = [];
        if (product.ram) line2Parts.push(product.ram);
        if (product.storage) line2Parts.push(product.storage + "GB");
        if (
          product.variants &&
          Array.isArray(product.variants) &&
          product.variants.length > 0
        ) {
          const variantValues = product.variants
            .map((v) => Object.values(v).join(", "))
            .join(", ");
          if (variantValues) line2Parts.push(variantValues);
        }
        line2Parts.push(product.used_mobile ? "Used" : "New");
        const line2 = line2Parts.filter((p) => p).join(" | ");

        return {
          id: product.id,
          name: line1 + "\n" + line2,
          product: product,
          unique_id: product.unique_id || null,
        };
      });

      setExchangeModels(formattedModels);
    } catch (error) {
      console.error("Error fetching exchange models:", error);
      setExchangeModels([]);
    } finally {
      setLoadingExchangeModels(false);
    }
  };

  // Fetch customers
  const fetchCustomers = async (search = "") => {
    setLoadingCustomers(true);
    try {
      const result = await customersAPI.getCustomers(search, 1, 50);
      const raw = result?.response ?? result;
      const list = Array.isArray(raw) ? raw : raw?.results ?? raw?.data ?? [];

      const formattedCustomers = (list || [])
        .map((c) => ({
          id: c.id ?? c.customer_id ?? c.pk,
          name: c.name ?? c.customer_name ?? c.user_name ?? "Unknown",
          customer: c,
        }))
        .filter((o) => o.id);

      setCustomers(formattedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Fetch Suppliers
  const fetchVendors = async (search = "") => {
    setLoadingVendors(true);
    try {
      const response = await vendorAPI.getVendorsList(search, 1, 50);
      const list = Array.isArray(response?.results)
        ? response.results
        : Array.isArray(response)
        ? response
        : [];

      const formattedVendors = (list || [])
        .map((v) => ({
          id: v.id ?? v.vendor_id ?? v.pk,
          name: v.name ?? v.vendor_name ?? "Unknown",
          vendor: v,
        }))
        .filter((o) => o.id);

      setVendors(formattedVendors);
    } catch (error) {
      console.error("Error fetching Suppliers:", error);
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };



  // Helper function to format product for display
  const formatProductForDisplay = (product) => {
    if (!product) return null;

    const line1Parts = [];
    if (product.name) line1Parts.push(product.name);
    if (product.suffix) line1Parts.push(product.suffix);
    const line1 = line1Parts.filter((p) => p).join(" ");

    const line2Parts = [];
    if (product.ram) line2Parts.push(product.ram);
    if (product.storage) line2Parts.push(product.storage + "GB");
    if (
      product.variants &&
      Array.isArray(product.variants) &&
      product.variants.length > 0
    ) {
      const variantValues = product.variants
        .map((v) => Object.values(v).join(", "))
        .join(", ");
      if (variantValues) line2Parts.push(variantValues);
    }
    line2Parts.push(product.used_mobile ? "Used" : "New");
    const line2 = line2Parts.filter((p) => p).join(" | ");

    return {
      id: product.id,
      name: line1 + "\n" + line2,
      product: product,
    };
  };

  // Load return data when editing
  useEffect(() => {
    if (isOpen && editReturn) {
      setLoadingReturnData(true);
      const loadReturnData = async () => {
        try {
          // Fetch full return data - this single API call contains all needed data
          const result = await returnRegisterAPI.getReturnById(editReturn.id);
          if (result.error) {
            toast.error(result.error.message || "Failed to load return data");
            return;
          }

          const returnData = result.response;

          // Set basic fields
          setReturnId(returnData.return_id || returnData.id || "");
          setImei(returnData.imei || "");
          setBuyingDate(
            returnData.buying_date
              ? formatDateForDisplay(new Date(returnData.buying_date))
              : ""
          );
          setDefectNote(returnData.note || returnData.defect_note || "");

          const actionValue = returnData.action
            ? returnData.action === "Change Device"
              ? "change_device"
              : returnData.action === "Keep for Repair"
              ? "keep_for_repair"
              : returnData.action === "Not Repairable"
              ? "not_repairable"
              : ""
            : "";
          setAction(actionValue);

          // Set status - map from API format to form format
          const statusValue = returnData.status
            ? returnData.status.toLowerCase().replace(" ", "_")
            : "";
          setStatus(statusValue);

          // Set second level action
          if (returnData.second_level_action) {
            const secondLevelValue =
              returnData.second_level_action === "Keep for Vendor"
                ? "keep_for_vendor"
                : returnData.second_level_action === "Keep for Repair"
                ? "keep_for_repair"
                : "";
            setSecondLevelAction(secondLevelValue);
          }

          // Set buying price
          if (returnData.buying_price) {
            setBuyingPrice(String(returnData.buying_price));
          }

          // Set IMEI validation (skip check in edit mode)
          if (returnData.imei) {
            setImeiFound(true);
            // Also set IMEI record if vendor info is available
            if (returnData.vendor || returnData.vendor_name) {
              setImeiRecord({
                supplier: returnData.vendor,
                supplier_name: returnData.vendor_name,
              });
            }
          }

          // Set model directly from API response (no additional API call needed)
          if (returnData.product) {
            const product =
              typeof returnData.product === "object"
                ? returnData.product
                : { id: returnData.product, name: returnData.product_name };

            if (product.id) {
              const formattedModel = formatProductForDisplay(product);
              if (formattedModel) {
                setSelectedModel(formattedModel);
              } else {
                // Fallback if product doesn't have full details
                setSelectedModel({
                  id: product.id,
                  name: returnData.product_name || `Product #${product.id}`,
                  product: product,
                });
              }
            }
          }

          // Set exchange model directly from API response
          if (returnData.replacement_product) {
            const replacementProduct =
              typeof returnData.replacement_product === "object"
                ? returnData.replacement_product
                : {
                    id: returnData.replacement_product,
                    name: returnData.replacement_product_name,
                  };

            if (replacementProduct.id) {
              const formattedModel = formatProductForDisplay(replacementProduct);
              if (formattedModel) {
                setExchangeModel(formattedModel);
              } else {
                // Fallback if product doesn't have full details
                setExchangeModel({
                  id: replacementProduct.id,
                  name:
                    returnData.replacement_product_name ||
                    `Product #${replacementProduct.id}`,
                  product: replacementProduct,
                });
              }
            }
          }

          // Set customer directly from API response
          if (returnData.customer) {
            const customer =
              typeof returnData.customer === "object"
                ? returnData.customer
                : { id: returnData.customer, name: returnData.customer_name };

            setSelectedCustomer({
              id: customer.id,
              name: customer.name || returnData.customer_name || "Unknown",
              customer: customer,
            });
          }

          // Set vendor if available
          if (returnData.vendor) {
            const vendor =
              typeof returnData.vendor === "object"
                ? returnData.vendor
                : { id: returnData.vendor, name: returnData.vendor_name };

            setSelectedVendor({
              id: vendor.id,
              name: vendor.name || returnData.vendor_name || "Unknown",
              vendor: vendor,
            });
          }
        } catch (error) {
          console.error("Error loading return data:", error);
          toast.error("Failed to load return data");
        } finally {
          setLoadingReturnData(false);
        }
      };

      loadReturnData();
    }
  }, [isOpen, editReturn]);

  // Fetch models when dropdown opens (lazy loading for edit mode)
  useEffect(() => {
    if (isModelDropdownOpen && models.length === 0) {
      fetchModels(modelSearch);
    }
  }, [isModelDropdownOpen]);

  // Search models
  useEffect(() => {
    if (isModelDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchModels(modelSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [modelSearch, isModelDropdownOpen]);

  // Auto-focus model search input when dropdown opens
  useEffect(() => {
    if (isModelDropdownOpen && modelSearchInputRef.current) {
      setTimeout(() => {
        modelSearchInputRef.current.focus();
      }, 0);
    }
  }, [isModelDropdownOpen]);

  // Fetch customers when dropdown opens (lazy loading for edit mode)
  useEffect(() => {
    if (isCustomerDropdownOpen && customers.length === 0) {
      fetchCustomers(customerSearch);
    }
  }, [isCustomerDropdownOpen]);

  // Search customers
  useEffect(() => {
    if (isCustomerDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchCustomers(customerSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [customerSearch, isCustomerDropdownOpen]);

  // Auto-focus customer search input when dropdown opens
  useEffect(() => {
    if (isCustomerDropdownOpen && customerSearchInputRef.current) {
      setTimeout(() => {
        customerSearchInputRef.current.focus();
      }, 0);
    }
  }, [isCustomerDropdownOpen]);

  // Fetch exchange models when dropdown opens (lazy loading for edit mode)
  useEffect(() => {
    if (isExchangeModelDropdownOpen && exchangeModels.length === 0) {
      fetchExchangeModels(exchangeModelSearch);
    }
  }, [isExchangeModelDropdownOpen]);

  // Search exchange models
  useEffect(() => {
    if (isExchangeModelDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchExchangeModels(exchangeModelSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [exchangeModelSearch, isExchangeModelDropdownOpen]);

  // Auto-focus exchange model search input when dropdown opens
  useEffect(() => {
    if (isExchangeModelDropdownOpen && exchangeModelSearchInputRef.current) {
      setTimeout(() => {
        exchangeModelSearchInputRef.current.focus();
      }, 0);
    }
  }, [isExchangeModelDropdownOpen]);

  // Fetch vendors when dropdown opens (lazy loading for edit mode)
  useEffect(() => {
    if (isVendorDropdownOpen && vendors.length === 0) {
      fetchVendors(vendorSearch);
    }
  }, [isVendorDropdownOpen]);

  // Search Suppliers
  useEffect(() => {
    if (isVendorDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchVendors(vendorSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [vendorSearch, isVendorDropdownOpen]);

  // Auto-set buying date when IMEI becomes valid
  // Clear buying date when IMEI is not found or cleared
  useEffect(() => {
    // Only auto-set/clear in non-edit mode
    if (!!editReturn) return;

    // If IMEI is empty, clear buying date
    if (!imei || imei.trim() === "") {
      setBuyingDate("");
      return;
    }

    // If IMEI check is still in progress, don't change buying date
    if (imeiChecking) return;

    // If IMEI is valid and found, set buying date
    if (isImeiValid && imeiRecord?.created_at) {
      // Use created_at from IMEI record, format to DD/MM/YYYY
      const createdDate = formatDateForDisplay(new Date(imeiRecord.created_at));
      setBuyingDate(createdDate);
    } else if (isImeiValid && !imeiRecord?.created_at) {
      // Fallback to today if created_at is not available, format to DD/MM/YYYY
      const today = formatDateForDisplay(new Date());
      setBuyingDate(today);
    } else if (!imeiFound) {
      // Clear buying date when IMEI is not found (after check completes)
      setBuyingDate("");
    }
  }, [isImeiValid, imeiRecord, imei, imeiFound, imeiChecking, editReturn]);

  // Auto-populate selling price from selected model when second level action is keep_for_vendor
  useEffect(() => {
    if (
      selectedModel &&
      selectedModel.product &&
      secondLevelAction === "keep_for_vendor"
    ) {
      const latestSellingPrice =
        selectedModel.product.latest_selling_price ||
        selectedModel.product.avg_selling_price ||
        "";

      if (
        latestSellingPrice !== "" &&
        latestSellingPrice !== null &&
        latestSellingPrice !== undefined
      ) {
        setBuyingPrice(String(latestSellingPrice));
      }
    }
  }, [selectedModel, secondLevelAction]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReturnId("");
      setImei("");
      setBuyingDate("");
      setSelectedModel(null);
      setSelectedCustomer(null);
      setDefectNote("");
      setAction("");
      setStatus("");
      setExchangeModel(null);
      setSecondLevelAction("");
      setBuyingPrice("");
      setModelSearch("");
      setCustomerSearch("");
      setExchangeModelSearch("");
      setVendorSearch("");
      setImeiFound(false);
      setImeiChecking(false);
      setImeiRecord(null);
      setSelectedVendor(null);
      setIsModelDropdownOpen(false);
      setIsCustomerDropdownOpen(false);
      setIsActionDropdownOpen(false);
      setIsExchangeModelDropdownOpen(false);
      setIsSecondLevelActionDropdownOpen(false);
      setIsVendorDropdownOpen(false);
      setIsSubmitting(false);
      setLoadingReturnData(false);
      setModels([]);
      setExchangeModels([]);
      setShowPrintModal(false);
      setSavedReturnData(null);
      setDefectNoteError("");
    }
  }, [isOpen]);

  // Filter models based on search
  const filteredModels = models.filter((model) =>
    model.name.toLowerCase().includes(modelSearch.toLowerCase())
  );

  // Use exchange models directly from API response (already filtered by search on server-side)
  const filteredExchangeModels = exchangeModels;

  // Filter customers based on search
  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Filter Suppliers based on search
  const filteredVendors = vendors.filter((vendor) =>
    vendor.name.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  // Auto-select exchange model when unique_id search returns exactly one result
  useEffect(() => {
    if (
      exchangeModelSearch &&
      exchangeModelSearch.trim() !== "" &&
      filteredExchangeModels.length === 1
    ) {
      const foundModel = filteredExchangeModels[0];
      // Check if search term matches unique_id exactly
      if (
        foundModel.unique_id &&
        foundModel.unique_id.toLowerCase() === exchangeModelSearch.toLowerCase()
      ) {
        setExchangeModel(foundModel);
        setIsExchangeModelDropdownOpen(false);
        setExchangeModelSearch("");
      }
    }
  }, [filteredExchangeModels, exchangeModelSearch]);

  // Check if "Change Device" is selected
  const isChangeDevice = action === "change_device";

  const handleSubmit = async () => {
    // Clear previous errors
    setDefectNoteError("");

    // Validation
    if (!imei || imei.trim() === "") {
      toast.error("IMEI is required");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Customer is required");
      return;
    }

    // If IMEI not found, defect note is mandatory
    if (!imeiFound && (!defectNote || defectNote.trim() === "")) {
      setDefectNoteError("Defect note is required when IMEI is not found");
      return;
    }

    if (!action) {
      toast.error("Action is required");
      return;
    }

    // Additional validation for "Change Device" action
    if (isChangeDevice) {
      if (!exchangeModel) {
        toast.error("Exchange Model is required");
        return;
      }

      // Supplier validation - check both IMEI record and selected Supplier
      if (!imeiFound && !selectedVendor) {
        toast.error("Supplier is required");
        return;
      }

      if (imeiFound && !imeiRecord?.supplier && !imeiRecord?.supplier_name) {
        toast.error(
          "Supplier information is required. Please ensure IMEI is valid."
        );
        return;
      }

      if (!secondLevelAction) {
        toast.error("2nd Level Action is required");
        return;
      }

      if (
        secondLevelAction === "keep_for_vendor" &&
        (!buyingPrice || buyingPrice.trim() === "")
      ) {
        toast.error(
          "Buying Price is required when Keep for Supplier is selected"
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Map action values to API format
      const actionMap = {
        change_device: "Change Device",
        keep_for_repair: "Keep for Repair",
        not_repairable: "Not Repairable",
      };

      // Map second level action values to API format
      const secondLevelActionMap = {
        keep_for_vendor: "Keep for Vendor",
        keep_for_repair: "Keep for Repair",
      };

      // Map form data to API request format
      const formData = {
        return_id: returnId || undefined,
        imei: imei.trim(),
        customer: selectedCustomer?.id || 0,
        action: actionMap[action] || action,
      };

      // Only include note if it's provided
      if (defectNote && defectNote.trim() !== "") {
        formData.note = defectNote.trim();
      }

      // Only include buying_date if it's provided
      if (buyingDate && buyingDate.trim() !== "") {
        formData.buying_date = convertDateToAPIFormat(buyingDate);
      }

      // Only include product if it's selected
      if (selectedModel?.product?.id || selectedModel?.id) {
        formData.product = selectedModel?.product?.id || selectedModel?.id;
      }

      // Additional fields for Change Device
      if (isChangeDevice) {
        // Use selected Supplier if IMEI not found, otherwise use IMEI record supplier
        formData.vendor = imeiFound
          ? imeiRecord?.supplier || 0
          : selectedVendor?.id || 0;
        formData.second_level_action =
          secondLevelActionMap[secondLevelAction] || secondLevelAction;
        formData.replacement_product =
          exchangeModel?.product?.id || exchangeModel?.id || 0;

        if (secondLevelAction === "keep_for_vendor" && buyingPrice) {
          formData.buying_price = buyingPrice.trim();
        }
      }

      if (onSubmit) {
        await onSubmit(formData, isEditMode ? editReturn.id : null);
      } else {
        // Default success message
        toast.success(
          isEditMode
            ? "Return updated successfully"
            : "Return registered successfully"
        );
        onClose();
      }
    } catch (error) {
      console.error("Error submitting return:", error);
      toast.error(
        isEditMode ? "Failed to update return" : "Failed to register return"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Create Label - Save return and show print modal
  const handleCreateLabel = async () => {
    // Clear previous errors
    setDefectNoteError("");

    // Validation (same as handleSubmit)
    if (!imei || imei.trim() === "") {
      toast.error("IMEI is required");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Customer is required");
      return;
    }

    // If IMEI not found, defect note is mandatory
    if (!imeiFound && (!defectNote || defectNote.trim() === "")) {
      setDefectNoteError("Defect note is required when IMEI is not found");
      return;
    }

    if (!action) {
      toast.error("Action is required");
      return;
    }

    // Additional validation for "Change Device" action
    if (isChangeDevice) {
      if (!exchangeModel) {
        toast.error("Exchange Model is required");
        return;
      }

      // Supplier validation - check both IMEI record and selected Supplier
      if (!imeiFound && !selectedVendor) {
        toast.error("Supplier is required");
        return;
      }

      if (imeiFound && !imeiRecord?.supplier && !imeiRecord?.supplier_name) {
        toast.error(
          "Supplier information is required. Please ensure IMEI is valid."
        );
        return;
      }

      if (!secondLevelAction) {
        toast.error("2nd Level Action is required");
        return;
      }

      if (
        secondLevelAction === "keep_for_vendor" &&
        (!buyingPrice || buyingPrice.trim() === "")
      ) {
        toast.error(
          "Buying Price is required when Keep for Supplier is selected"
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Map action values to API format
      const actionMap = {
        change_device: "Change Device",
        keep_for_repair: "Keep for Repair",
        not_repairable: "Not Repairable",
      };

      // Map second level action values to API format
      const secondLevelActionMap = {
        keep_for_vendor: "Keep for Vendor",
        keep_for_repair: "Keep for Repair",
      };

      // Map form data to API request format
      const formData = {
        return_id: returnId || undefined,
        imei: imei.trim(),
        customer: selectedCustomer?.id || 0,
        action: actionMap[action] || action,
      };

      // Only include note if it's provided
      if (defectNote && defectNote.trim() !== "") {
        formData.note = defectNote.trim();
      }

      // Only include buying_date if it's provided
      if (buyingDate && buyingDate.trim() !== "") {
        formData.buying_date = convertDateToAPIFormat(buyingDate);
      }

      // Only include product if it's selected
      if (selectedModel?.product?.id || selectedModel?.id) {
        formData.product = selectedModel?.product?.id || selectedModel?.id;
      }

      // Additional fields for Change Device
      if (isChangeDevice) {
        // Use selected Supplier if IMEI not found, otherwise use IMEI record supplier
        formData.vendor = imeiFound
          ? imeiRecord?.supplier || 0
          : selectedVendor?.id || 0;
        formData.second_level_action =
          secondLevelActionMap[secondLevelAction] || secondLevelAction;
        formData.replacement_product =
          exchangeModel?.product?.id || exchangeModel?.id || 0;

        if (secondLevelAction === "keep_for_vendor" && buyingPrice) {
          formData.buying_price = buyingPrice.trim();
        }
      }

      // Call API directly
      let result;
      if (isEditMode && editReturn?.id) {
        // Update existing return
        result = await returnRegisterAPI.updateReturn(editReturn.id, formData);
        if (result.error) {
          toast.error(result.error.message || "Failed to update return");
          return;
        }

        const successMessage =
          result.response?.message ||
          result.response?.success ||
          "Return updated successfully";
        toast.success(successMessage);
      } else {
        // Create new return
        result = await returnRegisterAPI.createReturn(formData);
        if (result.error) {
          toast.error(result.error.message || "Failed to register return");
          return;
        }

        const successMessage =
          result.response?.message ||
          result.response?.success ||
          "Return registered successfully";
        toast.success(successMessage);
      }

      // Get return data from API response
      const returnData = result.response || result.response?.data || {};

      // Prepare return data for print modal
      const printReturnData = {
        ...returnData,
        // Ensure all required fields are present
        return_id: returnData.return_id || returnData.id || returnId,
        imei: returnData.imei || imei,
        product: returnData.product || selectedModel?.product || selectedModel,
        product_name:
          returnData.product_name ||
          selectedModel?.name ||
          selectedModel?.product?.name,
        customer:
          returnData.customer || selectedCustomer?.customer || selectedCustomer,
        customer_name: returnData.customer_name || selectedCustomer?.name,
        note: returnData.note || defectNote,
        defect_note: returnData.defect_note || defectNote,
        created_at: returnData.created_at || new Date().toISOString(),
        date:
          returnData.date || returnData.created_at || new Date().toISOString(),
      };

      // Set saved return data and show print modal
      setSavedReturnData(printReturnData);
      setShowPrintModal(true);

      // Don't close main modal immediately - let it close when print modal closes
      // This ensures the component stays mounted so print modal can render
    } catch (error) {
      console.error("Error creating label:", error);
      toast.error(
        isEditMode ? "Failed to update return" : "Failed to register return"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Keep component mounted if print modal is open, even if main modal is closed
  if (!isOpen && !showPrintModal) return null;

  return (
    <>
      {/* Main Modal - Only show if isOpen is true and print modal is not open */}
      {isOpen && !showPrintModal && (
        <div
          className="returns-delete-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div className="returns-add-manual-modal">
            <div className="returns-modal-header">
              <div>
                <h3>{isEditMode ? "Edit Return" : "Return Registration"}</h3>
                <p>
                  {isEditMode
                    ? "Update return details below"
                    : "Register returns by filling details below"}
                </p>
              </div>
            </div>

            <div className="returns-modal-body">
              {loadingReturnData && isEditMode ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "2rem",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p>Loading return data...</p>
                </div>
              ) : (
                <>
                  {/* Return ID Field (Edit Mode Only) */}
                  {isEditMode && (
                    <div className="returns-form-group">
                      <label htmlFor="return-id-label">Return ID</label>
                      <input
                        id="returnId"
                        type="text"
                        className={` ${isPartsURL ? "returns-imei-input-parts" : "returns-imei-input"}`}
                        value={returnId}
                        readOnly
                        disabled
                      />
                    </div>
                  )}

                  {/* IMEI Field with Search */}
                  <div className="returns-form-group">
                    <label htmlFor="imei-label">
                      IMEI <span className="">*</span>
                    </label>
                    <div className="returns-imei-input-wrapper">
                      <input
                        id="imei"
                        type="text"
                        className={`${isPartsURL ? "returns-imei-input-parts" : "returns-imei-input"} ${
                          imei
                            ? isImeiValid || isEditMode
                              ? "valid"
                              : "invalid"
                            : ""
                        } ${imeiChecking ? "checking" : ""}`}
                        value={imei}
                        onChange={(e) => setImei(e.target.value)}
                        placeholder="Enter IMEI"
                      />
                      {imei && (
                        <div
                          className={`returns-imei-validation-icon ${
                            isImeiValid || isEditMode ? "" : "invalid"
                          }`}
                        >
                          {imeiChecking ? (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Checking...
                              </span>
                            </div>
                          ) : isImeiValid || isEditMode ? (
                            <GreenCheck />
                          ) : (
                            <X size={15} color="#fff" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buying Date Field with Date Picker */}
                  <div className="returns-form-group">
                    <label htmlFor="imei-label">Buying Date</label>
                    <div
                      className="returns-date-input-wrapper"
                      style={{ position: "relative" }}
                      ref={buyingDatePickerRef}
                    >
                      <img
                        src={CalendarIcon}
                        alt="Calendar"
                        className="returns-date-icon"
                        style={{ cursor: "pointer", zIndex: 1 }}
                        onClick={() =>
                          setShowBuyingDatePicker(!showBuyingDatePicker)
                        }
                      />
                      <input
                        type="text"
                        className={`${isPartsURL ? "returns-date-input-parts" : "returns-date-input"}`}
                        value={buyingDate}
                        readOnly
                        onClick={() =>
                          setShowBuyingDatePicker(!showBuyingDatePicker)
                        }
                        style={{ cursor: "pointer" }}
                        placeholder="DD/MM/YYYY"
                      />

                      {showBuyingDatePicker && (
                        <div
                          className="customer-detail-date-picker"
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            zIndex: 1000,
                          }}
                        >
                          <div className="date-picker-header">
                            <button
                              type="button"
                              className="date-picker-nav-btn"
                              onClick={() => {
                                const newMonth = new Date(buyingDateMonth);
                                newMonth.setMonth(
                                  buyingDateMonth.getMonth() - 1
                                );
                                setBuyingDateMonth(newMonth);
                              }}
                            >
                              ‹
                            </button>
                            <h3 className="date-picker-month-year">
                              {buyingDateMonth.toLocaleDateString("en-US", {
                                month: "long",
                                year: "numeric",
                              })}
                            </h3>
                            <button
                              type="button"
                              className="date-picker-nav-btn"
                              onClick={() => {
                                const newMonth = new Date(buyingDateMonth);
                                newMonth.setMonth(
                                  buyingDateMonth.getMonth() + 1
                                );
                                setBuyingDateMonth(newMonth);
                              }}
                            >
                              ›
                            </button>
                          </div>

                          <div className="date-picker-weekdays">
                            <div className="weekday">Mo</div>
                            <div className="weekday">Tu</div>
                            <div className="weekday">We</div>
                            <div className="weekday">Th</div>
                            <div className="weekday">Fr</div>
                            <div className="weekday">Sa</div>
                            <div className="weekday">Su</div>
                          </div>

                          <div className="date-picker-days">
                            {(() => {
                              const year = buyingDateMonth.getFullYear();
                              const month = buyingDateMonth.getMonth();
                              const firstDay = new Date(year, month, 1);
                              const lastDay = new Date(year, month + 1, 0);
                              const daysInMonth = lastDay.getDate();
                              const startingDayOfWeek =
                                (firstDay.getDay() + 6) % 7;
                              const days = [];
                              for (let i = 0; i < startingDayOfWeek; i++) {
                                days.push(null);
                              }
                              for (let day = 1; day <= daysInMonth; day++) {
                                days.push(new Date(year, month, day));
                              }
                              return days;
                            })().map((day, index) => {
                              const today = new Date();
                              today.setHours(23, 59, 59, 999);
                              const isDisabled = day && day > today;
                              const isSelected =
                                day &&
                                buyingDateObj &&
                                day.toDateString() ===
                                  buyingDateObj.toDateString();
                              return (
                                <div
                                  key={index}
                                  className={`${isPartsURL ? "date-picker-day-parts" : "date-picker-day"} ${
                                    day ? "" : "empty"
                                  } ${isDisabled ? "disabled" : ""} ${
                                    isSelected ? "selected" : ""
                                  }`}
                                  onClick={() => {
                                    if (day && !isDisabled) {
                                      setBuyingDateObj(day);
                                      setBuyingDate(
                                        day.toLocaleDateString("en-GB")
                                      );
                                      setShowBuyingDatePicker(false);
                                    }
                                  }}
                                >
                                  {day ? day.getDate() : ""}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Select Model Dropdown */}
                  <div className="returns-form-group">
                    <label htmlFor="imei-label">Select Model</label>
                    <div
                      className={`returns-dropdown ${
                        isModelDropdownOpen ? "open" : ""
                      }`}
                      ref={modelDropdownRef}
                    >
                      <button
                        type="button"
                        className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                          selectedModel ? "has-value" : ""
                        }`}
                        onClick={() => setIsModelDropdownOpen((v) => !v)}
                      >
                        <span className={selectedModel ? "has-value" : ""}>
                          {selectedModel
                            ? selectedModel.name
                                .split("\n")
                                .map((line, idx) => (
                                  <div
                                    key={idx}
                                    className={
                                      idx === 0
                                        ? "returns-product-name-line1"
                                        : "returns-product-name-line2"
                                    }
                                  >
                                    {line}
                                  </div>
                                ))
                            : "Select Model"}
                        </span>
                        <ChevronDown
                          size={16}
                          className="returns-dropdown-chevron"
                          color="#424A53"
                        />
                      </button>

                      {isModelDropdownOpen && (
                        <div className="returns-dropdown-menu">
                          <div className="returns-dropdown-search-container">
                            <div className={` ${isPartsURL ? "returns-dropdown-search-box-parts" : "returns-dropdown-search-box"}`}>
                              <Search size={16} color="#8C959F" />
                              <input
                                ref={modelSearchInputRef}
                                type="text"
                                placeholder="Search Model"
                                value={modelSearch}
                                onChange={(e) => setModelSearch(e.target.value)}
                                className="returns-dropdown-search-input"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <ul className="returns-dropdown-list">
                            {loadingModels ? (
                              <li className="returns-dropdown-loading">
                                Loading...
                              </li>
                            ) : filteredModels.length > 0 ? (
                              filteredModels.map((model) => (
                                <li key={model.id}>
                                  <button
                                    type="button"
                                    className={
                                      selectedModel?.id === model.id
                                        ? "active"
                                        : ""
                                    }
                                    onClick={() => {
                                      setSelectedModel(model);
                                      setIsModelDropdownOpen(false);
                                      setModelSearch("");
                                    }}
                                  >
                                    {model.name.split("\n").map((line, idx) => (
                                      <div
                                        key={idx}
                                        className={
                                          idx === 0
                                            ? "returns-product-name-line1"
                                            : "returns-product-name-line2"
                                        }
                                      >
                                        {line}
                                      </div>
                                    ))}
                                  </button>
                                </li>
                              ))
                            ) : (
                              <li className="returns-dropdown-no-results">
                                No models found
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Dropdown */}
                  <div className="returns-form-group">
                    <label htmlFor="imei-label">
                      Customer <span className="">*</span>
                    </label>
                    <div
                      className={`returns-dropdown ${
                        isCustomerDropdownOpen ? "open" : ""
                      }`}
                      ref={customerDropdownRef}
                    >
                      <button
                        type="button"
                        className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                          selectedCustomer ? "has-value" : ""
                        }`}
                        onClick={() => setIsCustomerDropdownOpen((v) => !v)}
                      >
                        <span className={selectedCustomer ? "has-value" : ""}>
                          {selectedCustomer?.name || "Select Customer"}
                        </span>
                        <ChevronDown
                          size={16}
                          className="returns-dropdown-chevron"
                          color="#424A53"
                        />
                      </button>

                      {isCustomerDropdownOpen && (
                        <div className="returns-dropdown-menu">
                          <div className="returns-dropdown-search-container">
                            <div className={`${isPartsURL ? "returns-dropdown-search-box-parts" : "returns-dropdown-search-box"}`}>
                              <Search size={16} color="#8C959F" />
                              <input
                                ref={customerSearchInputRef}
                                type="text"
                                placeholder="Search Customer"
                                value={customerSearch}
                                onChange={(e) =>
                                  setCustomerSearch(e.target.value)
                                }
                                className="returns-dropdown-search-input"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <ul className="returns-dropdown-list">
                            {loadingCustomers ? (
                              <li className="returns-dropdown-loading">
                                Loading...
                              </li>
                            ) : filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <li key={customer.id}>
                                  <button
                                    type="button"
                                    className={
                                      selectedCustomer?.id === customer.id
                                        ? "active"
                                        : ""
                                    }
                                    onClick={() => {
                                      setSelectedCustomer(customer);
                                      setIsCustomerDropdownOpen(false);
                                      setCustomerSearch("");
                                    }}
                                  >
                                    {customer.name}
                                  </button>
                                </li>
                              ))
                            ) : (
                              <li className="returns-dropdown-no-results">
                                No customers found
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Defect Note */}
                  <div className="returns-form-group">
                    <label htmlFor="imei-label">Defect Note</label>
                    <textarea
                      id="defectNote"
                      className={`${isPartsURL ? "returns-textarea-parts" : "returns-textarea"} ${defectNoteError ? "error" : ""}`}
                      value={defectNote}
                      onChange={(e) => {
                        setDefectNote(e.target.value);
                        // Clear error when user starts typing
                        if (defectNoteError) {
                          setDefectNoteError("");
                        }
                      }}
                      placeholder="Enter defect note"
                      rows="3"
                      style={defectNoteError ? { borderColor: "#FF4564" } : {}}
                    />
                    {defectNoteError && (
                      <span style={{ color: "#FF4564", fontSize: "12px", marginTop: "4px", display: "block" }}>
                        {defectNoteError}
                      </span>
                    )}
                  </div>

                  {/* Action Dropdown */}
                  <div className="returns-form-group">
                    <label htmlFor="imei-label">
                      {isChangeDevice ? "1st Level Action" : "Action"}{" "}
                      <span className="">*</span>
                    </label>
                    <div
                      className={`returns-dropdown ${
                        isActionDropdownOpen ? "open" : ""
                      }`}
                      ref={actionDropdownRef}
                    >
                      <button
                        type="button"
                        className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                          action ? "has-value" : ""
                        }`}
                        onClick={() => setIsActionDropdownOpen((v) => !v)}
                      >
                        <span className={action ? "has-value" : ""}>
                          {actionOptions.find((opt) => opt.value === action)
                            ?.label || "Select Action"}
                        </span>
                        <ChevronDown
                          size={16}
                          className="returns-dropdown-chevron"
                          color="#424A53"
                        />
                      </button>

                      {isActionDropdownOpen && (
                        <div className="returns-dropdown-menu">
                          <ul className="returns-dropdown-list">
                            {actionOptions.map((option) => (
                              <li key={option.value}>
                                <button
                                  type="button"
                                  className={
                                    action === option.value ? "active" : ""
                                  }
                                  onClick={() => {
                                    setAction(option.value);
                                    setIsActionDropdownOpen(false);
                                    // Reset change device specific fields if action changes
                                    if (option.value !== "change_device") {
                                      setExchangeModel(null);
                                      setSelectedVendor(null);
                                      setSecondLevelAction("");
                                      setBuyingPrice("");
                                      setExchangeModelSearch("");
                                    }
                                  }}
                                >
                                  {option.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Conditional fields for "Change Device" action */}
                  {isChangeDevice && (
                    <>
                      {/* Select Exchange Model Dropdown */}
                      <div className="returns-form-group">
                        <label htmlFor="exchange-model-label">
                          Select Exchange Model <span className="">*</span>
                        </label>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            className={`returns-dropdown ${
                              isExchangeModelDropdownOpen ? "open" : ""
                            }`}
                            ref={exchangeModelDropdownRef}
                            style={{ flex: 1 }}
                          >
                            <button
                              type="button"
                              className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                                exchangeModel ? "has-value" : ""
                              }`}
                              onClick={() =>
                                setIsExchangeModelDropdownOpen((v) => !v)
                              }
                            >
                              <span
                                className={exchangeModel ? "has-value" : ""}
                              >
                                {exchangeModel
                                  ? exchangeModel.name
                                      .split("\n")
                                      .map((line, idx) => (
                                        <div
                                          key={idx}
                                          className={
                                            idx === 0
                                              ? "returns-product-name-line1"
                                              : "returns-product-name-line2"
                                          }
                                        >
                                          {line}
                                        </div>
                                      ))
                                  : "Choose device model"}
                              </span>
                              <ChevronDown
                                size={16}
                                className="returns-dropdown-chevron"
                                color="#424A53"
                              />
                            </button>

                            {isExchangeModelDropdownOpen && (
                              <div className="returns-dropdown-menu">
                                <div className="returns-dropdown-search-container">
                                  <div className={`${isPartsURL ? "returns-dropdown-search-box-parts" : "returns-dropdown-search-box"}`}>
                                    <Search size={16} color="#8C959F" />
                                    <input
                                      ref={exchangeModelSearchInputRef}
                                      type="text"
                                      placeholder="Search Model"
                                      value={exchangeModelSearch}
                                      onChange={(e) =>
                                        setExchangeModelSearch(e.target.value)
                                      }
                                      className="returns-dropdown-search-input"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </div>
                                <ul className="returns-dropdown-list">
                                  {loadingExchangeModels ? (
                                    <li className="returns-dropdown-loading">
                                      Loading...
                                    </li>
                                  ) : filteredExchangeModels.length > 0 ? (
                                    filteredExchangeModels.map((model) => (
                                      <li key={model.id}>
                                        <button
                                          type="button"
                                          className={
                                            exchangeModel?.id === model.id
                                              ? "active"
                                              : ""
                                          }
                                          onClick={() => {
                                            setExchangeModel(model);
                                            setIsExchangeModelDropdownOpen(
                                              false
                                            );
                                            setExchangeModelSearch("");
                                          }}
                                        >
                                          {model.name
                                            .split("\n")
                                            .map((line, idx) => (
                                              <div
                                                key={idx}
                                                className={
                                                  idx === 0
                                                    ? "returns-product-name-line1"
                                                    : "returns-product-name-line2"
                                                }
                                              >
                                                {line}
                                              </div>
                                            ))}
                                        </button>
                                      </li>
                                    ))
                                  ) : (
                                    <li className="returns-dropdown-no-results">
                                      No models found
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>

                          <div style={{ width: "100%", textAlign: "right" }}>
                            {selectedModel &&
                              exchangeModel &&
                              (() => {
                                const modelPrice =
                                  selectedModel.product?.latest_selling_price ||
                                  0;
                                const exchangePrice =
                                  exchangeModel.product?.latest_selling_price ||
                                  0;
                                const priceDiff = modelPrice - exchangePrice;
                                const color =
                                  priceDiff < 0 ? "#FF383C" : "#0A8900";
                                return (
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <span style={{ color: "#424A53" }}>
                                      Price Difference:{" "}
                                    </span>
                                    <span style={{ color }}>
                                      €{priceDiff.toFixed(2)}
                                    </span>
                                  </span>
                                );
                              })()}
                          </div>
                        </div>
                      </div>

                      {/* Supplier - Show dropdown when IMEI not found, display when found */}
                      {!imeiFound ? (
                        <div className="returns-form-group">
                          <label htmlFor="vendor-label">
                            Supplier <span className="">*</span>
                          </label>
                          <div
                            className={`returns-dropdown ${
                              isVendorDropdownOpen ? "open" : ""
                            }`}
                            ref={vendorDropdownRef}
                          >
                            <button
                              type="button"
                              className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                                selectedVendor ? "has-value" : ""
                              }`}
                              onClick={() => setIsVendorDropdownOpen((v) => !v)}
                            >
                              <span
                                className={selectedVendor ? "has-value" : ""}
                              >
                                {selectedVendor?.name || "Select Supplier"}
                              </span>
                              <ChevronDown
                                size={16}
                                className="returns-dropdown-chevron"
                                color="#424A53"
                              />
                            </button>

                            {isVendorDropdownOpen && (
                              <div className="returns-dropdown-menu">
                                <div className="returns-dropdown-search-container">
                                  <div className={`${isPartsURL ? "returns-dropdown-search-box-parts" : "returns-dropdown-search-box"}`}>
                                    <Search size={16} color="#8C959F" />
                                    <input
                                      type="text"
                                      placeholder="Search Supplier"
                                      value={vendorSearch}
                                      onChange={(e) =>
                                        setVendorSearch(e.target.value)
                                      }
                                      className="returns-dropdown-search-input"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </div>
                                <ul className="returns-dropdown-list">
                                  {loadingVendors ? (
                                    <li className="returns-dropdown-loading">
                                      Loading Suppliers...
                                    </li>
                                  ) : filteredVendors.length === 0 ? (
                                    <li className="returns-dropdown-no-results">
                                      No Suppliers found
                                    </li>
                                  ) : (
                                    filteredVendors.map((vendor) => (
                                      <li key={vendor.id}>
                                        <button
                                          type="button"
                                          className={
                                            selectedVendor?.id === vendor.id
                                              ? "active"
                                              : ""
                                          }
                                          onClick={() => {
                                            setSelectedVendor(vendor);
                                            setIsVendorDropdownOpen(false);
                                            setVendorSearch("");
                                          }}
                                        >
                                          {vendor.name}
                                        </button>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="returns-form-group">
                          <label htmlFor="vendor-label">
                            Supplier <span className="">*</span>
                          </label>
                          <input
                            id="vendor"
                            type="text"
                            className={`${isPartsURL ? "returns-imei-input-parts" : "returns-imei-input"}`}
                            value={imeiRecord?.supplier_name || ""}
                            readOnly
                            disabled
                            style={{
                              backgroundColor: "#f6f8fa",
                              cursor: "not-allowed",
                            }}
                          />
                        </div>
                      )}

                      {/* 2nd Level Action Dropdown */}
                      <div className="returns-form-group">
                        <label htmlFor="second-level-action-label">
                          2nd Level Action <span className="">*</span>
                        </label>
                        <div
                          className={`returns-dropdown ${
                            isSecondLevelActionDropdownOpen ? "open" : ""
                          }`}
                          ref={secondLevelActionDropdownRef}
                        >
                          <button
                            type="button"
                            className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                              secondLevelAction ? "has-value" : ""
                            }`}
                            onClick={() =>
                              setIsSecondLevelActionDropdownOpen((v) => !v)
                            }
                          >
                            <span
                              className={secondLevelAction ? "has-value" : ""}
                            >
                              {secondLevelActionOptions.find(
                                (opt) => opt.value === secondLevelAction
                              )?.label || "Select Action"}
                            </span>
                            <ChevronDown
                              size={16}
                              className="returns-dropdown-chevron"
                              color="#424A53"
                            />
                          </button>

                          {isSecondLevelActionDropdownOpen && (
                            <div className="returns-dropdown-menu">
                              <ul className="returns-dropdown-list">
                                {secondLevelActionOptions.map((option) => (
                                  <li key={option.value}>
                                    <button
                                      type="button"
                                      className={
                                        secondLevelAction === option.value
                                          ? "active"
                                          : ""
                                      }
                                      onClick={() => {
                                        setSecondLevelAction(option.value);
                                        setIsSecondLevelActionDropdownOpen(
                                          false
                                        );
                                        // Reset buying price if not "Keep for Supplier"
                                        if (
                                          option.value !== "keep_for_vendor"
                                        ) {
                                          setBuyingPrice("");
                                        }
                                      }}
                                    >
                                      {option.label}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Buying Price Input (shown when "Keep for Supplier" is selected) */}
                      {secondLevelAction === "keep_for_vendor" && (
                        <div className="returns-form-group">
                          <label htmlFor="buying-price-label">
                            Selling Price
                          </label>
                          <input
                            id="buyingPrice"
                            type="text"
                            className={`${isPartsURL ? "returns-imei-input-parts" : "returns-imei-input"}`}
                            value={buyingPrice}
                            onChange={(e) => setBuyingPrice(e.target.value)}
                            placeholder="€0.0"
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div className="returns-modal-actions">
              <button
                type="button"
                className="returns-cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${isPartsURL ? "returns-create-label-btn-parts" : "returns-create-label-btn"}`}
                onClick={handleCreateLabel}
                disabled={
                  isSubmitting ||
                  !imei ||
                  !selectedCustomer ||
                  !action ||
                  (isChangeDevice &&
                    (!exchangeModel ||
                      (!imeiRecord?.supplier && !imeiRecord?.supplier_name) ||
                      !secondLevelAction ||
                      (secondLevelAction === "keep_for_vendor" &&
                        !buyingPrice)))
                }
              >
                <PrintIcon fill={isPartsURL ? "var(--primary-color-parts)" : "var(--primary-color-mobile)"} /> Create Label
              </button>
              <button
                type="button"
                className={`${isPartsURL ? "returns-save-btn-parts" : "returns-save-btn"}`}
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !imei ||
                  !selectedCustomer ||
                  !action ||
                  (isChangeDevice &&
                    (!exchangeModel ||
                      (!imeiRecord?.supplier && !imeiRecord?.supplier_name) ||
                      !secondLevelAction ||
                      (secondLevelAction === "keep_for_vendor" &&
                        !buyingPrice)))
                }
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditMode
                  ? "Update Return"
                  : "Save Return"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Return Register Modal - Render outside main modal overlay */}
      {showPrintModal && savedReturnData && (
        <PrintReturnRegister
          isOpen={showPrintModal}
          onClose={() => {
            setShowPrintModal(false);
            setSavedReturnData(null);

            // Close main modal when print modal closes
            onClose();
          }}
          returnData={savedReturnData}
        />
      )}
    </>
  );
}

export default AddManualReturnsModal;
